import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbSeparator, BreadcrumbPage } from "@/components/ui/breadcrumb";
import { Activity, Wind, Map as MapIcon, Radio, Crosshair, ChevronRight, Database, Satellite } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function OperationalPlanning() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const hudStats = [{
    label: "TRANSFORMATION INDEX",
    value: "94.2%",
    icon: <Crosshair className="size-4 text-primary" />
  }, {
    label: "DEPLOYMENT SPEED",
    value: "2.4X",
    icon: <Wind className="size-4 text-primary" />
  }, {
    label: "SYSTEM UPTIME",
    value: "99.99%",
    icon: <Satellite className="size-4 text-primary" />
  }, {
    label: "SECURITY POSTURE",
    value: "OPTIMAL",
    icon: <Activity className="size-4 text-primary" />
  }];
  return <section data-section-id="35530" id="our-expertise" className="bg-background text-foreground relative min-h-[85vh] flex flex-col justify-center overflow-hidden border-b border-border">
      {}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604077/img/tablet_flight_paths_weather.png)] bg-cover bg-center opacity-5 grayscale" />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background/60" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border))_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--border))_1px,transparent_1px)] bg-[size:50px_50px] opacity-30" />
      </div>

      {}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none z-10 overflow-hidden">
        <div className="absolute top-12 left-8 w-24 h-24 border-t-2 border-l-2 border-primary/20 rounded-tl-sm" />
        <div className="absolute bottom-12 right-8 w-24 h-24 border-b-2 border-r-2 border-primary/20 rounded-br-sm" />
        <div className="absolute top-1/2 right-12 w-1 h-32 bg-gradient-to-b from-transparent via-primary/10 to-transparent" />
        <div className="absolute bottom-24 left-12 flex gap-2">
           <div className="w-2 h-2 bg-primary/40 rounded-full animate-pulse" />
           <div className="w-2 h-2 bg-primary/20 rounded-full" />
           <div className="w-2 h-2 bg-primary/10 rounded-full" />
        </div>
      </div>

      <div className="container relative z-20 mx-auto px-4 py-12">
        {}
        <div className="mb-10 animate-in fade-in slide-in-from-top-4 duration-700">
          <div className="inline-flex items-center px-4 py-2 rounded-sm bg-white/80 backdrop-blur-md border border-border shadow-sm">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/HomePage" className="hover:text-primary transition-colors text-muted-foreground">Home</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator>
                  <ChevronRight className="size-3.5 text-muted-foreground" />
                </BreadcrumbSeparator>
                <BreadcrumbItem>
                  <BreadcrumbPage className="text-primary font-semibold">Services</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </div>

        {}
        <div ref={ref} className={`max-w-4xl transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="flex items-center gap-3 mb-4 text-primary font-mono text-xs tracking-widest uppercase font-semibold">
            <Radio className="size-4 animate-pulse" />
            <span>System Architecture Active</span>
            <div className="h-px w-12 bg-primary/20" />
          </div>

          <h1 className="text-5xl md:text-7xl lg:text-8xl font-extrabold tracking-tighter mb-6 text-foreground leading-[1.1]">
            Precision Engineering for <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-chart-2">
              Enterprise Performance
            </span>
          </h1>

          <p className="text-lg md:text-xl text-muted-foreground mb-10 max-w-2xl leading-relaxed border-l-2 border-primary/30 pl-6">
            Quantificial delivers execution-driven transformation for the modern enterprise. We architect and deploy intelligent systems that turn complex data and operations into measurable business performance.
          </p>

          {}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border mb-10 backdrop-blur-sm shadow-sm">
            {hudStats.map((stat, i) => <div key={i} data-index={i} className="bg-white/60 p-4 flex flex-col gap-2 hover:bg-white/90 transition-colors group">
                <div className="flex items-center justify-between text-muted-foreground text-[10px] font-mono tracking-widest font-bold">
                  {stat.label}
                  <span className="opacity-40 group-hover:opacity-100 transition-opacity">{stat.icon}</span>
                </div>
                <div className="text-xl font-bold text-foreground font-mono">
                  {stat.value}
                </div>
              </div>)}
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <Button asChild size="lg" className="h-14 px-8 text-base rounded-sm border-l-4 border-chart-1 bg-primary text-primary-foreground hover:bg-primary/90 transition-all shadow-md">
              <Link to="/ContactPage">
                <MapIcon className="mr-2 size-5" />
                Start a conversation
              </Link>
            </Button>
            
            <Button asChild variant="outline" size="lg" className="h-14 px-8 text-base rounded-sm border-border bg-white/50 text-foreground hover:bg-secondary/80 hover:text-primary hover:border-primary/30 transition-all backdrop-blur-sm">
              <Link to="/ServicesPage">
                <Database className="mr-2 size-5" />
                Explore Services
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}